import { generatePath, matchPath, useLocation, type Params } from 'react-router-dom';
import { SubscriptionPlans, SubscriptionPlansEnum } from '@features/subscription/entity';

export type AppRoute = {
  path: string;
  breadcrumb: string;
  allowedSubscriptionPlans: SubscriptionPlans[];
};
export type AppRoutePath = (typeof ROUTES)[keyof typeof ROUTES]['path'] | string;

export const buildHairDresserPath = (subPath: string): string => {
  return `/hairdresser/${subPath}`;
};

export const ROUTES: Record<string, AppRoute> = {
  clients: {
    path: 'clients',
    breadcrumb: 'Klienti',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  clientDetail: {
    path: 'clients/:clientId',
    breadcrumb: 'Detail klienta',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  visitDetail: {
    path: 'clients/:clientId/visits/:visitId',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
    breadcrumb: 'Detail návštěvy',
  },
  visits: {
    path: 'visits',
    breadcrumb: 'Všechny návštěvy',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  team: {
    path: 'team/:teamId',
    breadcrumb: 'Tým',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  shoppingList: {
    path: 'shopping-list',
    breadcrumb: 'Nákupní seznam',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  profile: {
    path: 'profile',
    breadcrumb: 'Můj profil',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  subscription: {
    path: 'subscription',
    breadcrumb: '',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  importContactsPage: {
    path: 'payment/import',
    breadcrumb: '',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  consumption: {
    path: 'consumption/:teamId',
    breadcrumb: 'Spotřeba',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  services: {
    path: 'services',
    breadcrumb: 'Služby',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  stock: {
    path: 'stock/:stockId',
    breadcrumb: 'Sklad',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  logs: {
    path: 'logs',
    breadcrumb: 'Záznamy o aktivitě',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  receivedPayments: {
    path: 'received-payments',
    breadcrumb: 'Obdržené platby',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.EXPERT],
  },
  usersOverview: {
    path: 'users-overview',
    breadcrumb: 'Přehled uživatelů',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  sms: {
    path: 'sms',
    breadcrumb: 'SMSky',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  pricingCalculator: {
    path: 'pricing-calculator',
    breadcrumb: 'Kalkulačka cen',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.EXPERT],
  },
  pendingReservations: {
    path: 'pending-reservations',
    breadcrumb: 'Žádosti o rezervaci',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.EXPERT],
  },
  home: {
    path: '',
    breadcrumb: 'Přehled',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
  notFound: {
    path: '*',
    breadcrumb: 'Nenalezeno',
    allowedSubscriptionPlans: [SubscriptionPlansEnum.PRO, SubscriptionPlansEnum.EXPERT],
  },
} as const;

export const Paths = {
  clientDetail: (clientId: string): AppRoutePath =>
    generatePath(buildHairDresserPath(ROUTES.clientDetail.path), { clientId }),

  visitDetail: (clientId: string, visitId: string): AppRoutePath =>
    generatePath(buildHairDresserPath(ROUTES.visitDetail.path), { clientId, visitId }),

  team: (teamId: string): AppRoutePath =>
    generatePath(buildHairDresserPath(ROUTES.team.path), { teamId }),

  stock: (stockId: string): AppRoutePath =>
    generatePath(buildHairDresserPath(ROUTES.stock.path), { stockId }),

  consumption: (teamId: string): AppRoutePath =>
    generatePath(buildHairDresserPath(ROUTES.consumption.path), { teamId }),
};

export type CurrentRoute = {
  key: keyof typeof ROUTES;
  path: string;
  breadcrumb: string;
  params: Params<string>;
} | null;

export const useCurrentRoute = (): CurrentRoute => {
  const location = useLocation();

  for (const [key, route] of Object.entries(ROUTES)) {
    const match = matchPath(
      { path: `/hairdresser/${route.path}`, end: route.path !== 'hairdresser/*' },
      location.pathname,
    );

    if (match) {
      return {
        key: key as keyof typeof ROUTES,
        path: route.path,
        breadcrumb: route.breadcrumb,
        params: match.params,
      };
    }
  }

  return null;
};
